import React, { useMemo, useState, useRef } from 'react';
import { leadZero } from '../../../../../../../../utils';
import { Tooltip } from 'antd';

interface DonutChartProps {
    /**
     * An array of exactly 3 numeric values (or more/less, logic supports generic array).
     */
    values: number[];
    quantidades: number[];
    /**
     * Optional size for the chart container. Defaults to 260px.
     */
    size?: number;
}

const COLORS = [
    '#7ebc61',
    '#FFD700',
    '#db4840',
    '#F5F5F5',
];

export const DonutChartPrazosSvg: React.FC<DonutChartProps> = ({ values, quantidades, size = 150 }) => {
    
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
    const containerRef = useRef<HTMLDivElement>(null);

    // Configuration for geometry
    const strokeWidth = size * 0.185; // ~18.5% thickness
    const gapPixels = size * 0.05;    // ~5% gap

    const radius = (size - strokeWidth) / 2;
    const center = size / 2;
    const circumference = 2 * Math.PI * radius;
    const total = values.reduce((acc, val) => acc + val, 0);

    // Calculate segments with overlap prevention
    const segments = useMemo(() => {
        if (total === 0) return [];

        const minAllocatedLength = strokeWidth + gapPixels;
        const rawLengths = values.map(v => (v / total) * circumference);

        let deficit = 0;
        const adjustedLengths = [...rawLengths];
        const largeIndices: number[] = [];

        adjustedLengths.forEach((len, i) => {
            if (len < minAllocatedLength) {
                deficit += (minAllocatedLength - len);
                adjustedLengths[i] = minAllocatedLength;
            } else {
                largeIndices.push(i);
            }
        });

        if (deficit > 0 && largeIndices.length > 0) {
            const totalLargeLength = largeIndices.reduce((sum, i) => sum + rawLengths[i], 0);
            largeIndices.forEach(i => {
                const ratio = rawLengths[i] / totalLargeLength;
                adjustedLengths[i] -= deficit * ratio;
            });
        }

        let accumulatedLength = 0;

        return adjustedLengths.map((allocatedLength, index) => {
            const strokeLength = Math.max(0, allocatedLength - gapPixels - strokeWidth);
            const startOffset = accumulatedLength + (gapPixels / 2) + (strokeWidth / 2);
            const rotationDeg = (startOffset / circumference) * 360;

            accumulatedLength += allocatedLength;

            return {
                color: COLORS[index % COLORS.length],
                strokeDasharray: `${strokeLength} ${circumference}`,
                rotation: rotationDeg - 90,
                originalValue: values[index]
            };
        });
    }, [values, total, circumference, gapPixels, strokeWidth]);

    const handleMouseMove = (e: React.MouseEvent, index: number) => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            // Calculate position relative to container
            setTooltipPos({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            });
            setHoveredIndex(index);
        }
    };

    const handleMouseLeave = () => {
        setHoveredIndex(null);
    };
    
    return (
        <div
            ref={containerRef}
            style={{ width: size, height: size, position: 'relative' }}
        >
            <svg
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                onMouseLeave={handleMouseLeave}
                style={{ overflow: 'visible' }}
            >
                {segments.map((seg, i) => {
                    if (!seg) return null;
                    const isHovered = hoveredIndex === i;
                        
                    return (
                        <circle
                            key={i}
                            cx={center}
                            cy={center}
                            r={radius}
                            fill="none"
                            stroke={seg.originalValue===0 ? "#f5f5f5" : seg.color}
                            strokeWidth={strokeWidth}
                            strokeLinecap="round"
                            strokeDasharray={seg.strokeDasharray}
                            strokeDashoffset={0}
                            transform={`rotate(${seg.rotation} ${center} ${center})`}
                            onMouseMove={(e) => handleMouseMove(e, i)}
                            style={{
                                transition: 'all',
                                transitionDuration: '0.3s',
                                cursor: 'pointer',
                                opacity: hoveredIndex !== null && !isHovered ? 0.4 : 1,
                                filter: brightness(isHovered, hoveredIndex, seg.originalValue),
                                transformOrigin: 'center',
                                // Slight scale effect on hover for better feedback
                                transform: isHovered
                                    ? `rotate(${seg.rotation}deg) scale(1.02)`
                                    : `rotate(${seg.rotation}deg) scale(1)`
                            }}
                        />
                    );
                })}
                
                <Tooltip title={'Execuções no prazo'} placement='left'>
                    <text
                        x="50%"
                        y="37%"
                        dy=".3em"
                        textAnchor="middle"
                        fill={COLORS?.[0]}
                        style={{ fontWeight: 700, fontSize: 18 }}
                    >
                        {leadZero(quantidades[0])}
                    </text>
                </Tooltip>
                <Tooltip title={'Execuções adiantadas'} placement='left'>
                    <text
                        x="50%"
                        y="50%"
                        dy=".3em"
                        textAnchor="middle"
                        fill={COLORS?.[1]}
                        style={{ fontWeight: 700, fontSize: 18 }}
                    >
                        {leadZero(quantidades[1])}
                    </text>
                </Tooltip>
                <Tooltip title={'Execuções atrasadas'} placement='left'>
                    <text
                        x="50%"
                        y="63%"
                        dy=".3em"
                        textAnchor="middle"
                        fill={COLORS?.[2]}
                        style={{ fontWeight: 700, fontSize: 18 }}
                    >
                        {leadZero(quantidades[2])}
                    </text>
                </Tooltip>
            </svg>

            {/* Floating Tooltip */}
            {/* 
         FIX: added 'pointer-events-none' to ensure the mouse 
         does not interact with the tooltip div, preventing the flicker loop.
      */}
            {hoveredIndex !== null && (
                <div
                    style={{
                        position: 'absolute',
                        left: tooltipPos.x,
                        top: tooltipPos.y,
                        transform: 'translate(15px, 15px)',
                        background: 'rgba(0, 0, 0, 0.8)',
                        padding: 7,
                        color: '#fff',
                        fontWeight: 500,
                        borderRadius: 5,
                        fontSize: 13,
                        zIndex:200
                    }}
                >
                    <span style={{margin:0, whiteSpace:'nowrap'}}>
                        {obterLabel(hoveredIndex)}: {Math.round((values[hoveredIndex] / total) * 100)}%
                    </span>
                </div>
            )}
        </div>
    );
};


function obterLabel(index: number) {
    switch (index) {
        case 0:
            return 'No prazo'
        case 1:
            return 'Adiantado'
        case 2:
            return 'Atrasado'
        case 3:
            return 'Não iniciado'
        default:
            break;
    }
};

function brightness(isHovered: boolean, hoveredIndex: number, value:number) {
    if (!isHovered) return 'none';
    if (hoveredIndex===3) return 'none';
    if (value===0) return 'none';
    return 'brightness(110%)';
}